import type {
  BuyflowWidget,
  GetNextBuyflowStepResponse,
  NotificationPreferencesWidget,
} from '@/lib/TypesOpenApi';
import type {PrefKeys} from '@/lib/types';
import {persist} from '@/lib/vatio-persistence';
import {useEffect, useState} from 'react';
import {proxy, useSnapshot} from 'valtio';
import {
  type StepWidgetStore,
  StepsPath,
  stepsMap,
} from '../routing/StepsRouting';
import {useWidget} from '../routing/StepsWizardContext';

const widget = stepsMap['NOTIFICATION_PREFERENCES']!.store;

export interface OrderEvent {
  isOrderEventSent: boolean;
}

export const orderEventStore = persist<OrderEvent>('order_event', {
  isOrderEventSent: false,
});

export interface NotificationPreferencesPageWidget {
  /** Base widget to relay on */
  widget: StepWidgetStore;
  content?: GetNextBuyflowStepResponse;
  /** synchronized version of widget. For internal usage within computed props */
  productWidget?: NotificationPreferencesWidget;
}

export const notificationPreferencesWidget =
  proxy<NotificationPreferencesPageWidget>({
    widget,
    get content() {
      return this.widget.content;
    },

    // will sync on load
    get productWidget() {
      // extract product info from widget
      const widgets =
        this.content?.buyflowStep.buyflowWidgets
          ?.map(({notificationPreferencesWidget: w}: BuyflowWidget) => w!)
          .filter((w: BuyflowWidget) => !!w) || [];
      return widgets[0];
    },
  });

// widget data expected to be loaded on previous page
export const useNotificationPreferencesWidget = () => {
  const [prodWidget, setProdWidget] = useState<
    NotificationPreferencesWidget | undefined
  >(undefined);
  useEffect(() => {
    const newWidget = notificationPreferencesWidget.productWidget;
    if (!newWidget || newWidget === prodWidget) {
      return;
    }
    setProdWidget(newWidget);
    // Preselect preferences
    const accountPreferences =
      newWidget.notificationPreferences?.accountUpdates?.preferences || [];
    const additionalPreferences =
      newWidget.notificationPreferences?.additionalEmails?.preferences || [];
    const preferences = accountPreferences?.concat(additionalPreferences);

    if (preferences.length) {
      preferences.map(pref =>
        setPreference(pref.preferenceType, !!pref.selected),
      );
    }
  });
  return useWidget(
    StepsPath.NotificationPreferences,
    notificationPreferencesWidget,
  );
};

export interface NotificationPreferencesState {
  preferences: Partial<{[key in PrefKeys]: boolean}>;

  // List of only enabled preferences
  enabled: Array<PrefKeys>;
}

// if add any new field, add the new field to the below reset function also
export const store = persist<NotificationPreferencesState>(
  StepsPath.NotificationPreferences,
  {
    preferences: {},

    get enabled() {
      return Object.keys(this.preferences).filter(
        key => this.preferences[key as PrefKeys],
      ) as PrefKeys[];
    },
  },
);

export const resetNotificationPreferencesState = () => {
  store.preferences = {};
  orderEventStore.isOrderEventSent = false;
};

export const togglePreference = (preference: PrefKeys) => {
  store.preferences[preference] = !store.preferences[preference];
};

export const setPreference = (preference: PrefKeys, isSelected: boolean) => {
  store.preferences[preference] = isSelected;
};

export const useNotificationPreferencesState = () => useSnapshot(store);
